import React, { useCallback, useEffect, useState } from 'react';
import { formatAmount, money } from '../../lib/money';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Card,
  ConfirmDialog,
  DescriptionList,
  EmptyState,
  KpiTile,
  PageContainer,
  PageHeader,
  Spinner,
  Textarea,
  useToast,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { catalogApi } from '../catalog/catalogApi';
import { stockTransferPath } from '../../lib/slug';
import { transfersApi, transferStatusMeta } from './transfersApi';
import '../requisitions/requisitions.css';
import './transfers.css';

const lineValue = (l) => (l.unitCost ? `${l.unitCost.currency} ${formatAmount(Number(l.unitCost.amount) * Number(l.quantity))}` : '—');

export default function TransferDetailPage() {
  const { id: routeRef } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const toast = useToast();
  const { hasPermission } = useProperty();
  const canTransfer = hasPermission('inventory.transfer');

  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [units, setUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setTransfer(await transfersApi.get(routeRef));
    } catch (err) {
      setError(err?.message || t('Transfer not found.'));
    } finally {
      setLoading(false);
    }
  }, [routeRef, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let active = true;
    catalogApi.listUnits().then((u) => { if (active) setUnits(u || []); }).catch(() => {});
    catalogApi.listLocations().then((l) => { if (active) setLocations(l || []); }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!transfer?.id) return;
    const canonical = stockTransferPath(transfer);
    if (`/transfers/${routeRef}` !== canonical) navigate(canonical, { replace: true });
  }, [transfer, routeRef, navigate]);

  const uomCode = (uid) => units.find((u) => u.id === uid)?.code;
  const locationName = (lid) => locations.find((l) => l.id === lid)?.name || '—';

  const doPost = async () => {
    setBusy(true);
    try {
      const posted = await transfersApi.post(transfer.id);
      setPostOpen(false);
      setTransfer(posted);
      toast.success(t('Transfer {number} posted', { number: posted.number }));
    } catch (err) {
      toast.error(t('Could not post transfer'), { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      const cancelled = await transfersApi.cancel(transfer.id, cancelReason.trim() || null);
      setCancelOpen(false);
      setCancelReason('');
      setTransfer(cancelled);
      toast.success(transfer.status === 'Posted' ? t('Transfer cancelled and reversed') : t('Draft transfer discarded'));
    } catch (err) {
      toast.error(t('Could not cancel transfer'), { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <PageContainer size="full"><div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div></PageContainer>;
  }
  if (error || !transfer) {
    return (
      <PageContainer size="full">
        <EmptyState icon={<Icon.CircleAlert size={28} />} title={t("Couldn't load transfer")} description={error || t('Not found.')}
          action={<Button variant="secondary" onClick={() => navigate('/transfers')}>{t('Back to transfers')}</Button>} />
      </PageContainer>
    );
  }

  const statusMeta = transferStatusMeta(transfer.status);
  const isDraft = transfer.status === 'Draft';
  const isPosted = transfer.status === 'Posted';
  const isCancelled = transfer.status === 'Cancelled';

  const headerActions = canTransfer && !isCancelled ? (
    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
      {isDraft && (
        <Button variant="primary" size="sm" leadingIcon={<Icon.Check size={14} />} onClick={() => setPostOpen(true)}>{t('Post transfer')}</Button>
      )}
      <Button variant="destructive" size="sm" leadingIcon={<Icon.Ban size={14} />} onClick={() => setCancelOpen(true)}>
        {isPosted ? t('Cancel & reverse') : t('Discard draft')}
      </Button>
    </div>
  ) : null;

  const overviewItems = [
    { term: t('Status'), description: <Badge tone={statusMeta.tone} dot>{t(statusMeta.label)}</Badge> },
    {
      term: t('Route'),
      description: (
        <span className="trf-route">
          <Badge tone="neutral">{locationName(transfer.fromLocationId)}</Badge>
          <Icon.ArrowRight size={14} />
          <Badge tone="brand">{locationName(transfer.toLocationId)}</Badge>
        </span>
      ),
    },
    { term: t('Transfer date'), description: transfer.transferDate ? new Date(transfer.transferDate).toLocaleDateString() : '—' },
    { term: isPosted ? t('Posted by') : t('Created by'), description: (isPosted ? transfer.postedByName : transfer.createdByName) || '—' },
    { term: isPosted ? t('Posted at') : t('Created at'), description: new Date(isPosted ? transfer.postedAt : transfer.createdAt).toLocaleString() },
  ];

  const renderLine = (l) => {
    const uom = uomCode(l.uomId) ? ` ${uomCode(l.uomId)}` : '';
    return (
      <div className="grn-line" key={l.lineId}>
        <div className="grn-line__item">
          <div className="grn-line__name">{l.description}</div>
          {l.sku && <div className="grn-line__sub">{l.sku}</div>}
        </div>
        <div className="grn-line__qty">
          <div className="grn-line__stat">
            <span className="grn-line__stat-label">{t('Quantity')}</span>
            <span className="grn-line__stat-value">{l.quantity}{uom}</span>
          </div>
          <div className="grn-line__stat">
            <span className="grn-line__stat-label">{t('Unit cost')}</span>
            <span className="grn-line__stat-value">{money(l.unitCost)}</span>
          </div>
          <div className="grn-line__stat">
            <span className="grn-line__stat-label">{t('Value')}</span>
            <span className="grn-line__stat-value">{lineValue(l)}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <PageContainer size="full">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/transfers')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Transfers')}
      </Button>

      <PageHeader
        title={isDraft ? t('Draft transfer') : t('Transfer {number}', { number: transfer.number })}
        description={<span className="req-detail__number">{locationName(transfer.fromLocationId)} → {locationName(transfer.toLocationId)}</span>}
        icon={<Icon.ArrowLeftRight size={22} />}
        actions={headerActions}
      />

      {isDraft && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="info">
            {t('This transfer is a draft — no stock has moved yet. Post it to move the items and update on-hand at both locations.')}
          </Banner>
        </div>
      )}

      {isCancelled && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="danger">
            {t('This transfer was cancelled')}{transfer.cancelledByName ? ` ${t('by {name}', { name: transfer.cancelledByName })}` : ''}{transfer.cancelledAt ? ` ${t('on {date}', { date: new Date(transfer.cancelledAt).toLocaleString() })}` : ''}.{transfer.cancelledReason ? ` ${t('Reason: {reason}', { reason: transfer.cancelledReason })}` : ''}
          </Banner>
        </div>
      )}

      <div className="req-kpis">
        <KpiTile label={t('Transfer value')} value={money(transfer.total)} />
        <KpiTile label={t('Items')} value={transfer.lineCount} />
        <KpiTile label={t('From')} value={locationName(transfer.fromLocationId)} />
        <KpiTile label={t('To')} value={locationName(transfer.toLocationId)} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <Card>
          <Card.Body>
            <DescriptionList items={overviewItems} layout="stacked" columns={2} />
          </Card.Body>
        </Card>

        {transfer.notes && (
          <Card>
            <Card.Header><Card.Title>{t('Notes')}</Card.Title></Card.Header>
            <Card.Body>{transfer.notes}</Card.Body>
          </Card>
        )}

        <Card>
          <Card.Header>
            <Card.Title>{t('Items')}</Card.Title>
            <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
              {isPosted
                ? t('These quantities were moved from the source to the destination.')
                : t('These quantities will move when the transfer is posted.')}
            </div>
          </Card.Header>
          <Card.Body>
            <div className="grn-lines">
              {(transfer.lines || []).map(renderLine)}
            </div>
          </Card.Body>
        </Card>
      </div>

      <ConfirmDialog
        open={postOpen}
        title={t('Post transfer')}
        description={t("Posting moves the items out of the source location and into the destination, carrying value at the source's moving-average cost. You can still reverse it afterwards.")}
        confirmLabel={t('Post transfer')}
        tone="primary"
        loading={busy}
        onConfirm={doPost}
        onCancel={() => (busy ? null : setPostOpen(false))}
      />

      <ConfirmDialog
        open={cancelOpen}
        title={isPosted ? t('Cancel & reverse transfer') : t('Discard draft transfer')}
        description={isPosted
          ? t('This sends the items back to the source location and restores both on-hand balances. It only works if the destination still holds the transferred stock.')
          : t('This voids the draft transfer. Nothing has been committed, so there is nothing to reverse.')}
        confirmLabel={isPosted ? t('Cancel transfer') : t('Discard draft')}
        tone="danger"
        loading={busy}
        onConfirm={doCancel}
        onCancel={() => (busy ? null : setCancelOpen(false))}
      >
        <Textarea rows={2} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder={t('Reason (optional)')} />
      </ConfirmDialog>
    </PageContainer>
  );
}
